import React, { createContext, FC, PropsWithChildren, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import ErrorDialog from "@/components/organisms/Dialogs/ErrorDialog/ErrorDialog";
import SuccessDialog from "@/components/organisms/Dialogs/SuccessDialog/SuccessDialog";
import { TOPIC_ERROR_DIALOG, usePubSub } from "@/shared/hooks/usePubSub";

interface IDialogContext {
    setOpen: (toggle: boolean) => void;
    setDialogProps: (props: DialogProps) => void;
    errorDialog: (options: ErrorOptions) => void;
    successDialog: (options: SuccessOptions) => void;
}

export const DialogContext = createContext(null);
DialogContext.displayName = "DialogContext";

export interface IDialogOptions {
    confirmButton?: string;
    redirectToBase?: boolean;
}

export interface SuccessOptions extends IDialogOptions {
    title: string;
    message: string;
}

export interface ErrorOptions extends IDialogOptions {
    errorCode?: string;
}

type DialogProviderProps = {
    initialOpen?: boolean;
};

enum DialogType {
    ERROR = "error",
    SUCCESS = "success",
}

type DialogProps = {
    title?: string;
    message?: string;
    errorCode?: string;
    confirmButton: string;
    redirectToBase: boolean;
    type: DialogType;
};

export const DialogProvider: FC<PropsWithChildren<DialogProviderProps>> = ({ children, initialOpen = false }) => {
    const [open, setOpen] = useState(initialOpen);
    const [dialogProps, setDialogProps] = useState<DialogProps>(null);
    const { t } = useTranslation();
    const { subscribe, unsubscribe } = usePubSub();

    const errorDialog = (options: ErrorOptions) => {
        setDialogProps({
            ...options,
            type: DialogType.ERROR,
            confirmButton: options.confirmButton || t("Common.Ok"),
            redirectToBase: options.redirectToBase ?? true,
        });
        setOpen(true);
    };

    useEffect(() => {
        subscribe(TOPIC_ERROR_DIALOG, errorDialog);
        return () => unsubscribe(TOPIC_ERROR_DIALOG);
    }, []);

    const handleClose = () => {
        setOpen(false);
    };

    const successDialog = (options: SuccessOptions) => {
        setDialogProps({
            ...options,
            type: DialogType.SUCCESS,
            confirmButton: options.confirmButton || t("Common.Ok"),
            redirectToBase: options.redirectToBase || true,
        });
        setOpen(true);
    };

    const contextValue = useMemo(
        (): IDialogContext => ({
            setOpen,
            setDialogProps,
            errorDialog,
            successDialog,
        }),
        [],
    );

    return (
        <DialogContext.Provider value={contextValue}>
            <>
                {dialogProps && dialogProps.type === DialogType.SUCCESS && (
                    <SuccessDialog
                        open={open}
                        onClose={handleClose}
                        title={dialogProps.title}
                        message={dialogProps.message}
                        {...dialogProps}
                    />
                )}
                {dialogProps && dialogProps.type === DialogType.ERROR && (
                    <ErrorDialog open={open} onClose={handleClose} {...dialogProps} />
                )}
                {children}
            </>
        </DialogContext.Provider>
    );
};
